import {
  ApprovalResponseItem,
  EventApprovalRequest,
  PendingCalendarItem,
} from '../../../contracts/FMPendingContracts';

/**
 * Pure builders for the FM OOF approval workflow (F4/F5).
 * Parity of `pendingevent-actions.controller.js` approveEvents/rejectEvents (build
 * EventApprovalRequest[]) + the bulk transaction-status tally in managePendingEvents.
 */

export interface ApprovalOptions {
  isApproved: boolean;
  declineComments?: string;
}

/** Build one EventApprovalRequest from a pending item (parity: approve/rejectEvents map). */
export function buildApprovalRequest(
  item: PendingCalendarItem,
  opts: ApprovalOptions
): EventApprovalRequest {
  return {
    RequestId: item.Id,
    DeclineComments: opts.isApproved ? '' : opts.declineComments || '',
    IsApproved: opts.isApproved,
    ShouldApproveOrDeclineSeries: false,
    // Parity: transformed events prefer EventStartDate/EventEndDate over StartDate/EndDate.
    StartDate: item.EventStartDate || item.StartDate,
    EndDate: item.EventEndDate || item.EndDate,
  };
}

/** Build EventApprovalRequest[] for single or bulk approve/reject. */
export function buildApprovalRequests(
  items: PendingCalendarItem[],
  opts: ApprovalOptions
): EventApprovalRequest[] {
  return items.map((i) => buildApprovalRequest(i, opts));
}

export interface ApprovalSummary {
  total: number;
  succeeded: number;
  failed: number;
  failedIds: Array<string | number>;
  allSucceeded: boolean;
}

/** Tally a ManageApprovalRequests response (parity: IsApprovedCounter / rejectedRequest). */
export function summarizeApprovalResponse(
  response: ApprovalResponseItem[]
): ApprovalSummary {
  const items = response || [];
  const failedIds: Array<string | number> = [];
  let succeeded = 0;
  for (const r of items) {
    if (r.IsSuccess) {
      succeeded += 1;
    } else {
      failedIds.push(r.RequestId);
    }
  }
  return {
    total: items.length,
    succeeded,
    failed: failedIds.length,
    failedIds,
    allSucceeded: items.length > 0 && failedIds.length === 0,
  };
}
